import * as React from 'react';
import { Formik, Form } from 'formik';
import { Link, NavLink } from 'react-router-dom';
import { STATIC_STORAGE_URL } from '@nexxt/common/constants';
import NotifyHOC from './HOC/Notifications';
import AuthContext from './AuthContext';
import Loader from './Loader';
import TextField from './TextField';
import Arrow from './Arrow';
import { LOGIN } from '../constants/routes';
import { forgotPasswordValidationSchema } from '../utils/validation';
import Logo from '../assets/SVG/nexxt-logo';
import Splash from './Splash';
import { SPLASH_CONTENT } from '../utils/constants';

interface ForgotPasswordProps {
    toastNotification: any;
}

interface ForgotPasswordState {
    email: string;
    isRequesting: boolean;
    isSent: boolean;
}

const ForgotPassword = (props: ForgotPasswordProps) => {
    const [isRequesting, setIsRequesting] = React.useState(false);
    const [isSent, setIsSent] = React.useState(false);
    const [isMobilePage, setIsMobilePage] = React.useState(false);
    const { firebaseAuth } = React.useContext(AuthContext);
    const [splashIndex, setSplashIndex] = React.useState(
        Math.round(Math.random() * (SPLASH_CONTENT.length - 1))
    );

    React.useEffect(() => {
        const mobileScreenUpdate = () => {
            const newWidth = window.innerWidth;
            if (newWidth < 1024) {
                setIsMobilePage(true);
            } else if (newWidth >= 1024) {
                setIsMobilePage(false);
            }
        };

        window.addEventListener('resize', mobileScreenUpdate);
        mobileScreenUpdate();

        return () => window.removeEventListener('resize', mobileScreenUpdate);
    }, []);

    const handleSubmit = async (values: any, { setSubmitting }) => {
        forgotPassword(values, setSubmitting);
    };

    const forgotPassword = async (values, setSubmitting) => {
        const { email } = values;
        setIsRequesting(true);

        try {
            await firebaseAuth.sendPasswordResetEmail(email);
            setIsRequesting(false);
            setIsSent(true);
            setSubmitting(false);
            props.toastNotification.success('Please check your email.');
        } catch (error) {
            if (error.code === 'auth/user-not-found') {
                props.toastNotification.error(
                    'Provided email is not associated with any account. Sign up or specify a registered email address.'
                );
            } else {
                props.toastNotification.error('');
            }
            setSubmitting(false);
            setIsRequesting(false);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-12">
            <div className="hidden lg:flex lg:col-span-6 xl:col-span-8 max-h-screen">
                <Splash index={splashIndex} />
            </div>
            <div className="col-span-12 lg:col-span-6 xl:col-span-4">
                <div className="auth--container login">
                    <div className="auth--wrapper">
                        <div className="auth--header flex flex-col items-center">
                            <NavLink exact={true} to={LOGIN}>
                                <Logo />
                            </NavLink>
                            <p className="auth--header--title">
                                Forgot your password?
                            </p>
                            <p className="auth--header--subtitlt">
                                Enter your email address and we&apos;ll send a
                                link to reset your password
                            </p>
                        </div>
                        <div className="auth--body">
                            {isSent ? (
                                <div className="form-confirmation">
                                    Email has been sent. Please check your email
                                </div>
                            ) : (
                                <Formik
                                    initialValues={{
                                        email: ''
                                    }}
                                    validationSchema={
                                        forgotPasswordValidationSchema
                                    }
                                    onSubmit={(values, actions) => {
                                        handleSubmit(values, actions);
                                    }}
                                    render={({
                                        values,
                                        touched,
                                        handleChange,
                                        handleBlur,
                                        errors
                                    }) => (
                                        <Form className="registration-forms">
                                            <TextField
                                                type="text"
                                                name="email"
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Email"
                                                label="Email"
                                                errorMessage={errors.email}
                                                touchedValue={touched.email}
                                            />

                                            <button
                                                disabled={isRequesting}
                                                type="submit"
                                                className="form-submit__button"
                                            >
                                                Send Link
                                            </button>
                                        </Form>
                                    )}
                                />
                            )}
                            <div className="form-group form-link mt-4 text-center">
                                <Link to={LOGIN} className="form-link">
                                    {`< Back to login`}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="page--byline hidden lg:block">
                <a
                    className="by-logo"
                    href="https://nexxt.in"
                    target="_blank"
                    rel="noreferrer"
                >
                    <img
                        src={`${STATIC_STORAGE_URL}/img/powered_by_nexxt_intelligence_inca.png`}
                    />
                </a>
            </div>
        </div>
    );
};

export default NotifyHOC(ForgotPassword);
